import {
  CommerceToolsCart,
  CustomLineItem,
  LineItem,
  LineItemGroup,
  SprdConfigurationDetails,
} from '@/types/commercetools';
import { EMBROIDERY_PRINT_TYPE_IDS } from '@/utility/constants';
import { AppError } from '../errorClasses';

const getCustomLineItem = (cart: CommerceToolsCart, key: string): CustomLineItem | undefined => {
  return cart.customLineItems.find((item) => item.key === key);
};

export const getDecorationLineItem = (
  lineItem: LineItem,
  cart: CommerceToolsCart
): CustomLineItem | undefined => {
  return getCustomLineItem(
    cart,
    `decoration-${lineItem.variant.sku}-${lineItem.custom.fields.sprdProductLookupId}`
  );
};

export const getCommissionLineItem = (
  lineItem: LineItem,
  cart: CommerceToolsCart
): CustomLineItem | undefined => {
  return getCustomLineItem(
    cart,
    `commission-${lineItem.variant.sku}-${lineItem.custom.fields.sprdProductLookupId}`
  );
};

export const getProductTypeId = (lineItem: LineItem) => {
  const productTypeId = lineItem.variant.attributes.find(
    (attr) => attr.name === 'sprdProductTypeId'
  )?.value;
  if (!productTypeId) {
    throw new AppError('Cannot find productTypeId in variant.attributes', 404);
  }
  return productTypeId;
};

export const getAppearanceId = (lineItem: LineItem) => {
  const appearanceId = lineItem.variant.attributes.find(
    (attr) => attr.name === 'sprdAppearanceId'
  )?.value;
  if (appearanceId === undefined) {
    throw new AppError('Cannot find appearanceId in variant.attributes', 404);
  }
  return appearanceId;
};

export const getAppearanceName = (
  appearances: ProductType['appearances'],
  appearanceId: string | number
) => {
  return appearances.find((appearance) => appearance.id === String(appearanceId))?.name ?? '';
};

export const parseConfigurationDetails = (
  item: LineItem | CustomLineItem | undefined
): SprdConfigurationDetails | undefined => {
  const configurationDetails = item?.custom.fields.sprdConfigurationDetails;
  if (!configurationDetails) return undefined;

  try {
    return JSON.parse(configurationDetails) as SprdConfigurationDetails;
  } catch {
    return undefined;
  }
};

export const isEmbroideryConfiguration = (
  configurationDetails: SprdConfigurationDetails | undefined
) => {
  const printTypeIds =
    configurationDetails?.configurations?.map((configuration) => configuration.printTypeId) ?? [];
  return EMBROIDERY_PRINT_TYPE_IDS.some((id) => printTypeIds.includes(id));
};

export const getSizeId = (lineItem: LineItem) => {
  const sizeId = lineItem.variant.attributes.find((attr) => attr.name === 'sprdSizeId')?.value;
  if (!sizeId) {
    throw new AppError('Cannot find sizeId in variant.attributes', 404);
  }
  return sizeId;
};

export const getSizeName = (sizes: ProductType['sizes'], sizeId: string | number) => {
  return sizes.find((size) => size.id === String(sizeId))?.name ?? '';
};

export const getEditLink = (lineItemGroup: LineItemGroup, cartId: string | undefined) => {
  const firstLineItem = lineItemGroup.lineItems[0];
  const editLink = firstLineItem?.custom.fields.sprdEditLink;
  if (!editLink || !cartId) return undefined;
  return new URL(
    //TODO: remove later
    (editLink.startsWith('http') ? editLink : `https://${window.location.hostname}${editLink}`)
      .replace('{BASKET_ID}', cartId)
      .replace('{BASKET_ITEM_ID}', lineItemGroup.lineItems[0]!.id)
  );
};

export const getTotalQuantity = (lineItemGroups: LineItemGroup[] | undefined) => {
  if (!lineItemGroups) return 0;
  return lineItemGroups.reduce((acc, group) => acc + group.groupQuantity, 0);
};

export const getGiftWrappingLineItem = (lineItems: LineItem[]) => {
  return lineItems.find((lineItem) => {
    const key = 'giftwrapping-fee';
    return lineItem.productKey === key || lineItem.variant.sku === key;
  });
};
